/**
 * 首页: 继续跟读 / 最近看过 / 会员状态.
 *
 * 资源列表来自接口(登录即可读); 系统没有学习计划或打卡能力,
 * 首页只展示真实存在的续播进度与资源导航, 不造「今日任务」这类计划概念。
 * 未开通会员时顶部保留开通引导, 下方仍展示资源列表(免费资源带徽章)。
 */

import { ApiError, api } from '../lib/api'
import { FREE_BADGE_CLASS, TAG_BADGE_CLASS } from '../lib/badges'
import { courseContent } from '../lib/content-language'
import { ensureCourseAccess } from '../lib/entitlement'
import { t } from '../lib/i18n'
import { goTo } from '../stores/router'
import { authRef } from '../stores/auth'
import type { Course, RecentCourseProgress } from '../types/api'

/** 首页最近看过资源的展示数量 (桌面 2 列时刚好三行) */
const HOME_COURSE_COUNT = 6
/** 首页"热门资源"默认条数, 与后端 /courses/popular 的默认值一致 */
const HOME_POPULAR_COUNT = 10

/** 首页卡片: 普通资源或带跟读进度的最近看过资源 */
type HomeCourse = Course | RecentCourseProgress

export function homePage() {
  return {
    ...courseContent(),
    courses: [] as Course[],
    courseTotal: 0,
    /** 热门资源(按全站打开人数排序), 新用户也能看到内容 */
    popular: [] as Course[],
    // 最近看过(带跟读进度): 首页卡片与续播入口以它为准
    recent: [] as RecentCourseProgress[],
    loading: false,
    loaded: false,
    error: '',

    async load(): Promise<void> {
      if (!authRef()?.isAuthenticated) return
      this.loading = true
      this.error = ''
      try {
        // 最近看过单独拉, 失败不影响资源列表
        const [page, recent, popular] = await Promise.all([
          api.courses.list({ limit: 20 }),
          api.progress.recent(HOME_COURSE_COUNT + 1).catch(() => [] as RecentCourseProgress[]),
          // 热门是独立板块, 拿不到就不展示, 不影响其余内容
          api.courses.popular(HOME_POPULAR_COUNT).catch(() => [] as Course[]),
        ])
        this.courses = page.items
        this.courseTotal = page.meta.total
        this.recent = recent
        this.popular = popular
        this.loaded = true
      } catch (error) {
        this.error = error instanceof ApiError ? error.message : '资源加载失败'
      } finally {
        this.loading = false
      }
    },

    /** 最近看过: 排除会员顶部续播卡片, 不用未看过的资源补齐. */
    get visibleCourses(): HomeCourse[] {
      const heroId = authRef()?.isMember ? this.resumeCourse?.id : undefined
      return this.recent.filter((course) => course.id !== heroId).slice(0, HOME_COURSE_COUNT)
    },

    /** 续播入口: 优先最近看过的资源, 没有则取第一个上架资源 */
    get resumeCourse(): HomeCourse | undefined {
      return this.recent[0] ?? this.courses[0]
    },

    /** 续播卡片标题 */
    get resumeTitle(): string {
      const course = this.resumeCourse
      return course ? this.titleOf(course) : t('home.emptyTitle')
    },

    get resumeCover(): string {
      return this.coverSrc(this.resumeCourse)
    },

    /** 只有真正看过一部分时才展示进度, 避免把「未开始」伪装成学习计划 */
    get resumeHasProgress(): boolean {
      const course = this.resumeCourse
      return !!course && 'position_seconds' in course && course.position_seconds > 0
    },

    get resumePercent(): number {
      return this.resumeCourse ? this.progressPercentOf(this.resumeCourse) : 0
    },

    get resumeRemainingMinutes(): number {
      const course = this.resumeCourse
      if (!course || !('position_seconds' in course)) return 0
      return Math.max(1, Math.ceil((course.duration_seconds - course.position_seconds) / 60))
    },

    get resumeMeta(): string {
      const course = this.resumeCourse
      if (!course) return ''
      return [
        [course.source, course.speaker].filter(Boolean).join(' · '),
        t('home.minutesSentences', {
          minutes: this.minutesOf(course),
          sentences: course.sentence_count,
        }),
      ]
        .filter(Boolean)
        .join(' · ')
    },

    get resumeCta(): string {
      if (!this.resumeCourse) return t('home.browse')
      return this.resumeHasProgress ? t('home.continue') : t('home.start')
    },

    get resumeEyebrow(): string {
      if (!this.resumeCourse) return t('home.memberOnly')
      return this.resumeHasProgress ? t('home.lastWatched') : t('home.recommended')
    },

    /** 热门资源: 排除续播卡片上正在展示的那条, 避免同一资源在首页出现两次 */
    get popularCourses(): Course[] {
      const heroId = authRef()?.isMember ? this.resumeCourse?.id : undefined
      return this.popular.filter((course) => course.id !== heroId)
    },

    get hasMoreCourses(): boolean {
      return this.courseTotal > this.visibleCourses.length + (this.resumeCourse ? 1 : 0)
    },

    minutesOf(course: Course): number {
      return Math.max(1, Math.round(course.duration_seconds / 60))
    },

    /** 继续学习进度: 未开始或无有效总时长时为 0, 最多显示 100%. */
    progressPercentOf(course: HomeCourse): number {
      const position = 'position_seconds' in course ? course.position_seconds : 0
      if (!Number.isFinite(position) || course.duration_seconds <= 0) return 0
      return Math.min(100, Math.max(0, Math.round((position / course.duration_seconds) * 100)))
    },

    tagClass: TAG_BADGE_CLASS,
    freeBadgeClass: FREE_BADGE_CLASS,

    /** 主 CTA: 进入跟读页(优先最近看过的资源) */
    startPractice(): void {
      const first = this.resumeCourse
      if (!first) {
        goTo('courses')
        return
      }
      if (!ensureCourseAccess(first)) return
      goTo('practice', { id: String(first.id) })
    },

    openCourse(course: Course): void {
      if (!ensureCourseAccess(course)) return
      goTo('practice', { id: String(course.id) })
    },

    openCourses(): void {
      goTo('courses')
    },
  }
}
